import { KeyboardEvent, RefObject, useRef } from 'react'

import { isDefined } from 'aem-libs-ts/src/utils/common/isDefined'


type KeyActionsFromHook = ReturnType<typeof createKeyActionMap>

type KeyActionApp = keyof KeyActionsFromHook | 'Escape' | 'Home' | 'End' | 'Tab' | 'Enter' | 'Space'

type KeyActions = Partial<Record<KeyActionApp, (keyboardEvent: KeyboardEvent<HTMLElement>, index: number) => void>>

interface UseFocusingListOptions<T extends HTMLElement> {
  onSelect?: (index: number) => void
  disableChangeOnFocus?: boolean
  customElementRef?: RefObject<T>
  customKeyActions?: KeyActions
}

const activeElement = (children: Element[]) => {
  const activeElementIndex = children.findIndex((child) => child.getAttribute?.('aria-checked') === 'true')
  return activeElementIndex >= 0 ? activeElementIndex : 0
}

const createKeyActionMap = <T extends HTMLElement>(
  role: string,
  itemCount: number,
  onFinish: (index: number) => void,
  onSelect?: (index: number) => void
) => {
  const nextItemElement = (index: number) => {
    if (role !== 'group') {
      onFinish((index + 1) % itemCount)
    }
  }

  const previousItemElement = (index: number) => {
    if (role !== 'group') {
      onFinish((index - 1 + itemCount) % itemCount)
    }
  }

  const selectItemElement = (index: number, keyboardEvent: KeyboardEvent) => {
    keyboardEvent && keyboardEvent.preventDefault()
    onSelect && onSelect(index)
  }
  return {
    ArrowDown: (_e: KeyboardEvent<T>, index: number) => {
      nextItemElement(index)
    },
    ArrowRight: (_e: KeyboardEvent<T>, index: number) => {
      nextItemElement(index)
    },
    ArrowUp: (_e: KeyboardEvent<T>, index: number) => {
      previousItemElement(index)
    },
    ArrowLeft: (_e: KeyboardEvent<T>, index: number) => {
      previousItemElement(index)
    },
    Enter: (keyboardEvent: KeyboardEvent<T>, index: number) => {
      selectItemElement(index, keyboardEvent)
    },
    Space: (keyboardEvent: KeyboardEvent<T>, index: number) => {
      selectItemElement(index, keyboardEvent)
    }
  }
}

const getFocusedIndexFromDOM = (children: Element[]) => children.findIndex((child) => child === document.activeElement)

const getListChildren = <T extends HTMLElement>(eleRef: RefObject<T>) => Array.from(eleRef.current?.children || [])

const isElement = (value: unknown): value is HTMLElement => isDefined(value) && value instanceof HTMLElement

const focusElement = <T extends HTMLElement>(elementRef: RefObject<T>, index?: number) => {
  const children = getListChildren(elementRef)
  const targetIndex =
    typeof index === 'number' && index >= 0 && index < children.length ? index : activeElement(children)
  children.forEach((child, id) => {
    if (isElement(child) && id === targetIndex) {
      child.focus()
    }
  })
}

export function useFocusingList<T extends HTMLElement>({
  onSelect,
  disableChangeOnFocus,
  customKeyActions
}: UseFocusingListOptions<T>) {
  const elementRef = useRef<T | null>(null)

  const handleKeyDown = (keyboardEvent: KeyboardEvent<T>) => {
    const index = disableChangeOnFocus
      ? getFocusedIndexFromDOM(getListChildren(elementRef as RefObject<T>))
      : activeElement(getListChildren(elementRef as RefObject<T>))

    const finishFocus = (index: number) => {
      focusElement(elementRef as RefObject<T>, index)
      !disableChangeOnFocus && onSelect && onSelect(index)
      keyboardEvent.preventDefault()
    }

    const keyAction = {
      ...createKeyActionMap(
        elementRef?.current?.role || '',
        getListChildren(elementRef as RefObject<T>).length,
        finishFocus,
        onSelect
      ),
      ...customKeyActions
    }
    if (keyAction) {
      keyAction[keyboardEvent.code as keyof typeof keyAction]?.(keyboardEvent, index)
    }
  }

  return {
    elementRef,
    handleKeyDown
  }
}
